import React, { useState } from 'react';
import useTotalEnergy from '../hooks/useTotalEnergy';
import useTotalSessions from '../hooks/useTotalSessions';
import useTotalDistinctVehicles from '../hooks/useTotalDistinctVehicles';
import useTotalTimeByGroup from '../hooks/useTotalTimeByGroup';
import useTotalRevenue from '../hooks/useTotalRevenue';
import { formatINRNumber } from "../utils/formatINR";

// Utility: Convert seconds to hours:minutes
const formatSecondsToHMS = (seconds) => {
  if (isNaN(seconds) || seconds <= 0) return '0hr : 00min';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}hr : ${minutes.toString().padStart(2, '0')}min`;
};

const SectionOne = ({ tiles = [], onTileClick, customRange }) => {
  const [selectedTile, setSelectedTile] = useState('Total revenue (₹)');

  // Fetch data using hooks with customRange
  const { totalEnergy, isLoading: isLoadingEnergy } = useTotalEnergy(customRange);
  const { totalSessions, isLoading: isLoadingSessions } = useTotalSessions(customRange);
  const { totalUniqueVehicles, isLoading: isLoadingUniqueVehicles } = useTotalDistinctVehicles(customRange);
  const { totalChargingTime, isLoading: isLoadingTotalChargingTime } = useTotalTimeByGroup(customRange);
  const { totalRevenue, isLoading: isLoadingRevenue } = useTotalRevenue(customRange);

  const handleTileClick = (title) => {
    setSelectedTile(title);
    if (onTileClick) onTileClick(title);
  };

  const defaultTiles = [
    { 
      title: 'Total revenue (₹)', 
      value: isLoadingRevenue ? 'Loading...' : formatINRNumber(totalRevenue)
    },
    { 
      title: 'Total energy (kW)', 
      value: isLoadingEnergy ? 'Loading...' : totalEnergy?.toLocaleString(undefined, { maximumFractionDigits: 1 })
    },
    { 
      title: 'Total sessions', 
      value: isLoadingSessions ? 'Loading...' : totalSessions?.toLocaleString()
    },
    { 
      title: 'Total hours', 
      value: isLoadingTotalChargingTime ? 'Loading...' : formatSecondsToHMS(totalChargingTime)
    },
    { 
      title: 'Unique vehicles', 
      value: isLoadingUniqueVehicles ? 'Loading...' : totalUniqueVehicles?.toLocaleString()
    },
  ];

  const allTiles = tiles.length > 0 ? tiles : defaultTiles;

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {allTiles.map((tile, index) => {
          const isSelected = selectedTile === tile.title;

          return (
            <div
              key={tile.title}
              onClick={() => handleTileClick(tile.title)}
              className={`
                flex flex-col items-center justify-center px-4 py-5 rounded-xl border bg-white transition-[box-shadow,border-color] duration-200 cursor-pointer
                ${isSelected
                  ? 'border-brand-300 ring-1 ring-brand-200 shadow-lg'
                  : 'border-neutral-200 shadow-card hover:border-neutral-300 hover:shadow-lg'}
              `}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <p className="text-2xl font-semibold tracking-tight tabular-nums text-brand-950 text-center mb-1">
                {tile.value}
              </p>
              <h6 className="text-13 font-medium text-neutral-500 text-center">
                {tile.title}
              </h6>
              {tile.change && (
                <p className="mt-1 text-xs font-medium text-green-700 text-center">
                  {tile.change}
                </p>
              )}
              {isSelected && (
                <div className="mt-3 w-8 h-1 rounded-full bg-brand-600" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SectionOne;
